import { useEffect, useState, useCallback } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import type { SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import { Button } from '@/shared/components/ui/button'
import { Switch } from '@/shared/components/ui/switch'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Separator } from '@/shared/components/ui/separator'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import {
  useCreateReporteWhatsApp,
  useReporteWhatsAppPorCampania,
  useRespuestasWhatsApp,
  useCreateRespuestaWhatsApp,
  useUpdateRespuestaWhatsApp,
  useDeleteRespuestaWhatsApp,
  useReportesDisponiblesWhatsApp,
} from '@/modules/cronograma/hooks/useReportes'
import type { RespuestaWhatsApp } from '@/modules/cronograma/types/reporte'
import { useToast } from '@/shared/hooks/use-toast'
import { ImageUpload } from '@/shared/components/ui/image-upload'
import { uploadFile } from '@/shared/services/uploads'
import { Loader2, MessageSquare, BarChart, FileText, Flag, FileSpreadsheet, Plus, Pencil, Trash2, MessageCircle, Calendar, Link2 } from 'lucide-react'

// Schema de validación para reporte WhatsApp
const reporteWhatsAppSchema = z.object({
  campania_id: z.number(),
  // Métricas principales WhatsApp
  total_mensajes: z.number().min(0, 'Debe ser mayor o igual a 0'),
  enviados: z.number().min(0, 'Debe ser mayor o igual a 0'),
  entregados: z.number().min(0, 'Debe ser mayor o igual a 0'),
  leidos: z.number().min(0, 'Debe ser mayor o igual a 0'),
  clics: z.number().min(0, 'Debe ser mayor o igual a 0'),
  errores: z.number().min(0, 'Debe ser mayor o igual a 0'),
  // Información general
  titulo_campana: z.string().optional().default(''),
  titulo_resumen: z.string().optional().default(''),
  descripcion: z.string().optional().default(''),
  base_dirigida: z.string().optional().default(''),
  resumen: z.string().optional().default(''),
  // Contenido del mensaje
  mensaje_texto: z.string().optional().default(''),
  imagen_url: z.string().optional().default(''),
  // Botones CTA
  botones_cta: z.array(z.object({
    texto: z.string().optional().default(''),
    url: z.string().url('URL inválida').optional().or(z.literal('')),
  })).optional().default([]),
  // Cronogramas
  cronogramas_wa: z.array(z.object({
    fecha: z.string(),
    hora: z.string(),
    descripcion: z.string().optional().default(''),
  })).optional().default([]),
  // Redirección
  reporte_relacionado_id: z.number().optional(),
  // Conclusión
  conclusion: z.string().optional().default(''),
  // Estado de recopilación
  completado: z.boolean(),
  dias_recopilacion: z.number().nullable().optional(),
  // Excel
  habilitar_boton_excel: z.boolean(),
  url_excel: z.string().url('URL inválida').optional().or(z.literal('')),
}).superRefine((data, ctx) => {
  // Si NO es redirección, validar campos requeridos
  if (!data.reporte_relacionado_id) {
    if (!data.titulo_campana || data.titulo_campana.trim() === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Título es requerido', path: ['titulo_campana'] })
    }
  }
})

type ReporteWhatsAppFormValues = z.infer<typeof reporteWhatsAppSchema>

interface ReporteWhatsAppModalProps {
  open: boolean
  onClose: () => void
  campaniaId: number
  nombreCampania: string
  nombreLaboratorio: string
  fechaPublicacion: string | null
  tieneReporte: boolean
}

export function ReporteWhatsAppModal({
  open,
  onClose,
  campaniaId,
  nombreCampania,
  nombreLaboratorio,
  fechaPublicacion,
  tieneReporte
}: ReporteWhatsAppModalProps) {
  const { toast } = useToast()
  const createReporte = useCreateReporteWhatsApp()
  const { data: reportesDisponibles = [] } = useReportesDisponiblesWhatsApp()

  // Estado para carga de imagen
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  // Estado para el modal de respuestas
  const [respuestaModalOpen, setRespuestaModalOpen] = useState(false)
  const [respuestaEditando, setRespuestaEditando] = useState<RespuestaWhatsApp | null>(null)
  const [mensajeRespuesta, setMensajeRespuesta] = useState('')

  // Siempre intentar cargar el reporte WhatsApp cuando el modal está abierto
  const { data: reporteExistente, isLoading: cargandoReporte, refetch } = useReporteWhatsAppPorCampania(
    campaniaId,
    open // Cargar siempre que el modal esté abierto
  )

  // Cargar respuestas del reporte
  const { data: respuestas = [], refetch: refetchRespuestas } = useRespuestasWhatsApp(
    reporteExistente?.id || null,
    open && !!reporteExistente?.id
  )

  // Mutations para respuestas
  const createRespuesta = useCreateRespuestaWhatsApp()
  const updateRespuesta = useUpdateRespuestaWhatsApp()
  const deleteRespuesta = useDeleteRespuestaWhatsApp()

  // Refrescar datos cuando se abre el modal
  useEffect(() => {
    if (open && campaniaId) {
      refetch()
    }
  }, [open, campaniaId, refetch])

  const form = useForm<ReporteWhatsAppFormValues>({
    resolver: zodResolver(reporteWhatsAppSchema) as any,
    defaultValues: {
      campania_id: campaniaId,
      total_mensajes: 0,
      enviados: 0,
      entregados: 0,
      leidos: 0,
      clics: 0,
      errores: 0,
      titulo_campana: nombreCampania,
      titulo_resumen: '',
      descripcion: '',
      base_dirigida: '',
      resumen: '',
      mensaje_texto: '',
      imagen_url: '',
      botones_cta: [],
      cronogramas_wa: [],
      reporte_relacionado_id: undefined,
      conclusion: '',
      completado: false,
      habilitar_boton_excel: false,
      url_excel: '',
    },
  })

  const { fields: botonesFields, append: appendBoton, remove: removeBoton } = useFieldArray({
    control: form.control,
    name: 'botones_cta',
  })

  const { fields: cronogramasFields, append: appendCronograma, remove: removeCronograma } = useFieldArray({
    control: form.control,
    name: 'cronogramas_wa',
  })

  // Callback para ImageUpload
  const handleImageChange = useCallback((file: File | null, previewUrl: string | null) => {
    setPendingFile(file)
    if (file) {
      setImageUrl(previewUrl)
    } else {
      setImageUrl(null)
    }
  }, [])

  // Cargar datos del reporte existente cuando se obtienen
  useEffect(() => {
    if (reporteExistente && open) {
      // Convertir cronogramas del backend a formato del form
      const cronogramasForm = (reporteExistente.cronogramas || []).map((c: any) => {
        const dt = new Date(c.fecha_hora)
        return {
          fecha: dt.toISOString().split('T')[0],
          hora: dt.toTimeString().slice(0, 5),
          descripcion: c.descripcion || '',
        }
      })

      setImageUrl(reporteExistente.imagen_url || null)
      setPendingFile(null)

      form.reset({
        campania_id: campaniaId,
        total_mensajes: reporteExistente.base || 0,
        enviados: reporteExistente.base || 0,
        entregados: reporteExistente.entrega || 0,
        leidos: reporteExistente.leidos || 0,
        clics: reporteExistente.clics || 0,
        errores: reporteExistente.unsub || 0,
        titulo_campana: reporteExistente.titulo_campana || nombreCampania,
        titulo_resumen: reporteExistente.titulo_resumen || '',
        descripcion: reporteExistente.descripcion || '',
        base_dirigida: reporteExistente.base_dirigida || '',
        resumen: reporteExistente.resumen || '',
        mensaje_texto: reporteExistente.mensaje_texto || '',
        imagen_url: reporteExistente.imagen_url || '',
        botones_cta: reporteExistente.botones_cta || [],
        cronogramas_wa: cronogramasForm,
        reporte_relacionado_id: reporteExistente.reporte_relacionado_id ?? undefined,
        conclusion: reporteExistente.conclusion || '',
        completado: reporteExistente.completado || false,
        dias_recopilacion: reporteExistente.dias_recopilacion ?? undefined,
        habilitar_boton_excel: reporteExistente.habilitar_boton_excel || false,
        url_excel: reporteExistente.url_excel || '',
      })
    }
  }, [reporteExistente, open, campaniaId, nombreCampania, form])

  // Actualizar campania_id cuando cambia (solo si no hay reporte existente)
  useEffect(() => {
    if (!reporteExistente) {
      form.setValue('campania_id', campaniaId)
      form.setValue('titulo_campana', nombreCampania)
    }
  }, [campaniaId, nombreCampania, form, reporteExistente])

  // Calcular tasas en tiempo real
  const total_mensajes = form.watch('total_mensajes')
  const enviados = form.watch('enviados')
  const entregados = form.watch('entregados')
  const leidos = form.watch('leidos')
  const clics = form.watch('clics')
  const errores = form.watch('errores')
  const completado = form.watch('completado')
  const habilitarExcel = form.watch('habilitar_boton_excel')
  const reporteRelacionadoId = form.watch('reporte_relacionado_id')
  const esRedireccion = !!reporteRelacionadoId && reporteRelacionadoId > 0

  const tasa_envio = total_mensajes > 0 ? ((enviados / total_mensajes) * 100).toFixed(2) : '0.00'
  const tasa_entrega = enviados > 0 ? ((entregados / enviados) * 100).toFixed(2) : '0.00'
  const tasa_lectura = entregados > 0 ? ((leidos / entregados) * 100).toFixed(2) : '0.00'
  const tasa_clics = leidos > 0 ? ((clics / leidos) * 100).toFixed(2) : '0.00'
  const tasa_error = total_mensajes > 0 ? ((errores / total_mensajes) * 100).toFixed(2) : '0.00'

  // Log errores de validación
  const onError = (errors: any) => {
    console.error('Errores de validación:', errors)
  }

  const onSubmit: SubmitHandler<ReporteWhatsAppFormValues> = async (data) => {
    try {
      // Subir imagen pendiente si existe
      let finalImageUrl = imageUrl
      if (pendingFile) {
        setIsUploading(true)
        try {
          const fechaStr = fechaPublicacion ? fechaPublicacion.replace(/-/g, '') : ''
          const customName = `${nombreLaboratorio}_${nombreCampania}${fechaStr ? `_${fechaStr}` : ''}`
          const uploadResponse = await uploadFile(pendingFile, 'app/multichannel', customName)
          finalImageUrl = uploadResponse.url
        } catch (error) {
          toast({
            title: 'Error',
            description: 'No se pudo subir la imagen. Intente nuevamente.',
            variant: 'destructive',
          })
          setIsUploading(false)
          return
        }
        setIsUploading(false)
      }

      let reporteData: any

      if (data.reporte_relacionado_id) {
        // Reporte de redirección: solo datos mínimos
        reporteData = {
          campania_id: data.campania_id,
          reporte_relacionado_id: data.reporte_relacionado_id,
          titulo_campana: data.titulo_campana || nombreCampania,
          base: 0,
          entrega: 0,
          leidos: 0,
          clics: 0,
          unsub: 0,
          completado: false,
          habilitar_boton_excel: false,
        }
      } else {
        // Reporte normal: todos los datos
        reporteData = {
          campania_id: data.campania_id,
          base: data.total_mensajes,
          entrega: data.entregados,
          leidos: data.leidos,
          clics: data.clics,
          unsub: data.errores,
          titulo_campana: data.titulo_campana,
          titulo_resumen: data.titulo_resumen || undefined,
          descripcion: data.descripcion || undefined,
          base_dirigida: data.base_dirigida || '',
          resumen: data.resumen || undefined,
          mensaje_texto: data.mensaje_texto || '',
          imagen_url: finalImageUrl || undefined,
          botones_cta: data.botones_cta && data.botones_cta.length > 0 ? data.botones_cta : undefined,
          conclusion: data.conclusion || '',
          completado: data.completado,
          dias_recopilacion: data.dias_recopilacion ?? undefined,
          habilitar_boton_excel: data.habilitar_boton_excel,
          url_excel: data.url_excel || undefined,
          // Cronogramas inline: el backend borra los existentes y los recrea
          cronogramas_wa: (data.cronogramas_wa || [])
            .filter((c) => c.fecha && c.hora)
            .map((c, i) => ({
              fecha_hora: `${c.fecha}T${c.hora}:00`,
              orden: i + 1,
              descripcion: c.descripcion || undefined,
            })),
        }
      }

      await createReporte.mutateAsync(reporteData)

      toast({
        title: tieneReporte ? 'Reporte actualizado' : 'Reporte creado',
        description: tieneReporte
          ? 'El reporte de campaña WhatsApp se actualizó exitosamente'
          : 'El reporte de campaña WhatsApp se creó exitosamente',
      })
      onClose()
      form.reset()
      setPendingFile(null)
      setImageUrl(null)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo crear el reporte. Intente nuevamente.',
        variant: 'destructive',
      })
    }
  }

  // Handlers para respuestas
  const handleAbrirModalRespuesta = (respuesta?: RespuestaWhatsApp) => {
    if (respuesta) {
      setRespuestaEditando(respuesta)
      setMensajeRespuesta(respuesta.mensaje)
    } else {
      setRespuestaEditando(null)
      setMensajeRespuesta('')
    }
    setRespuestaModalOpen(true)
  }

  const handleGuardarRespuesta = async () => {
    if (!mensajeRespuesta.trim()) {
      toast({
        title: 'Error',
        description: 'El mensaje es requerido',
        variant: 'destructive',
      })
      return
    }

    if (!reporteExistente?.id) {
      toast({
        title: 'Error',
        description: 'Primero debe guardar el reporte antes de agregar respuestas',
        variant: 'destructive',
      })
      return
    }

    try {
      if (respuestaEditando) {
        await updateRespuesta.mutateAsync({
          id: respuestaEditando.id,
          data: {
            mensaje: mensajeRespuesta,
          }
        })
        toast({ title: 'Respuesta actualizada' })
      } else {
        await createRespuesta.mutateAsync({
          reporte_id: reporteExistente.id,
          mensaje: mensajeRespuesta,
          tipo: 'neutral',
          telefono: 'N/A',
          fecha_recibida: new Date().toISOString(),
        })
        toast({ title: 'Respuesta agregada' })
      }
      setRespuestaModalOpen(false)
      refetchRespuestas()
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo guardar la respuesta',
        variant: 'destructive',
      })
    }
  }

  const handleEliminarRespuesta = async (id: number) => {
    if (!confirm('¿Está seguro de eliminar esta respuesta?')) return

    try {
      await deleteRespuesta.mutateAsync(id)
      toast({ title: 'Respuesta eliminada' })
      refetchRespuestas()
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la respuesta',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-green-600" />
            {tieneReporte ? 'Editar' : 'Crear'} Reporte de WhatsApp
          </DialogTitle>
          <DialogDescription>{nombreCampania}</DialogDescription>
        </DialogHeader>

        {cargandoReporte ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-green-600" />
            <span className="ml-2 text-muted-foreground">Cargando datos del reporte...</span>
          </div>
        ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, onError)} className="space-y-6">
            {/* Redirección */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart className="h-5 w-5 text-green-600" />
                  {esRedireccion ? 'Redirección de Reporte' : 'Métricas de WhatsApp'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="reporte_relacionado_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Link2 className="h-4 w-4" />
                        Redirigir a Reporte
                      </FormLabel>
                      <Select onValueChange={(value) => field.onChange(value && value !== '0' ? Number(value) : undefined)} value={field.value?.toString()}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="No redirigir" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="0">No redirigir</SelectItem>
                          {reportesDisponibles.map((reporte) => (
                            <SelectItem key={reporte.id} value={reporte.id.toString()}>
                              {reporte.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        {esRedireccion
                          ? 'Este reporte redirigirá al reporte seleccionado. No se requieren datos adicionales.'
                          : 'Seleccione un reporte existente para redirigir, o deje en "No redirigir" para crear un reporte completo.'}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!esRedireccion && (
                  <>
                    <Separator />
                    <div className="grid grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="total_mensajes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Total Mensajes</FormLabel>
                            <FormControl>
                              <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="enviados"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Enviados</FormLabel>
                            <FormControl>
                              <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="entregados"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Entregados</FormLabel>
                            <FormControl>
                              <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="leidos"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Leídos</FormLabel>
                            <FormControl>
                              <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="clics"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Clics</FormLabel>
                            <FormControl>
                              <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="errores"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Errores</FormLabel>
                            <FormControl>
                              <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Métricas Calculadas */}
                    <Separator />
                    <div className="grid grid-cols-5 gap-4">
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground mb-1">Tasa de Envío</p>
                        <p className="text-2xl font-bold text-blue-600">{tasa_envio}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground mb-1">Tasa de Entrega</p>
                        <p className="text-2xl font-bold text-green-600">{tasa_entrega}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground mb-1">Tasa de Lectura</p>
                        <p className="text-2xl font-bold text-cyan-600">{tasa_lectura}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground mb-1">Tasa de Clics</p>
                        <p className="text-2xl font-bold text-amber-600">{tasa_clics}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground mb-1">Tasa de Error</p>
                        <p className="text-2xl font-bold text-red-600">{tasa_error}%</p>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {!esRedireccion && (
            <>
            {/* Información General */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5 text-green-600" />
                  Información General
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="titulo_campana"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Título de la Campaña</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="titulo_resumen"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Título Resumen (opcional)</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Título breve para el resumen" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="descripcion"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Descripción (opcional)</FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={2} placeholder="Descripción general del reporte" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="base_dirigida"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Base Dirigida</FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={2} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="resumen"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Resumen (opcional)</FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={2} placeholder="Resumen ejecutivo del reporte" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Contenido del Mensaje WhatsApp */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-green-600" />
                  Contenido del Mensaje
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="mensaje_texto"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mensaje de WhatsApp</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          rows={4}
                          placeholder="Texto del mensaje enviado por WhatsApp"
                        />
                      </FormControl>
                      <FormDescription>
                        El contenido del mensaje tal como se envió a los destinatarios
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormItem>
                  <FormLabel>Imagen/Media (opcional)</FormLabel>
                  <FormControl>
                    <ImageUpload
                      value={imageUrl || undefined}
                      onChange={handleImageChange}
                      disabled={isUploading}
                    />
                  </FormControl>
                  <FormDescription>
                    Arrastra o selecciona la imagen adjunta al mensaje
                  </FormDescription>
                </FormItem>

                {/* Botones CTA */}
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <FormLabel>Botones CTA</FormLabel>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => appendBoton({ texto: '', url: '' })}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Agregar Botón
                    </Button>
                  </div>
                  {botonesFields.map((field, index) => (
                    <div key={field.id} className="flex gap-3 items-start">
                      <FormField
                        control={form.control}
                        name={`botones_cta.${index}.texto`}
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormControl>
                              <Input {...field} placeholder="Texto del botón" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`botones_cta.${index}.url`}
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormControl>
                              <Input type="url" {...field} placeholder="https://..." />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeBoton(index)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Cronogramas WhatsApp */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-green-600" />
                  Cronogramas de Envío
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">Fechas y horarios de envío del mensaje</p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => appendCronograma({ fecha: '', hora: '', descripcion: '' })}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Agregar Cronograma
                  </Button>
                </div>
                {cronogramasFields.map((field, index) => (
                  <div key={field.id} className="flex gap-3 items-start border rounded-lg p-3">
                    <FormField
                      control={form.control}
                      name={`cronogramas_wa.${index}.fecha`}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormLabel className={index > 0 ? 'sr-only' : ''}>Fecha</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`cronogramas_wa.${index}.hora`}
                      render={({ field }) => (
                        <FormItem className="w-32">
                          <FormLabel className={index > 0 ? 'sr-only' : ''}>Hora</FormLabel>
                          <FormControl>
                            <Input type="time" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`cronogramas_wa.${index}.descripcion`}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormLabel className={index > 0 ? 'sr-only' : ''}>Descripción</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Ej: Envío principal" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="mt-6"
                      onClick={() => removeCronograma(index)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                {cronogramasFields.length === 0 && (
                  <p className="text-sm text-center text-muted-foreground py-4">
                    No hay cronogramas agregados
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Conclusión */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5 text-green-600" />
                  Conclusión
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="conclusion"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Conclusión</FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={3} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Estado de Recopilación */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Flag className="h-5 w-5 text-green-600" />
                  Estado de Recopilación
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <FormLabel>Recopilación finalizada</FormLabel>
                    <FormDescription>Marcar cuando se deje de recopilar información</FormDescription>
                  </div>
                  <FormField
                    control={form.control}
                    name="completado"
                    render={({ field }) => (
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    )}
                  />
                </div>
                {completado && (
                  <FormField
                    control={form.control}
                    name="dias_recopilacion"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Días de recopilación</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)} placeholder="Ej: 15" />
                        </FormControl>
                        <FormDescription>Días desde el lanzamiento</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </CardContent>
            </Card>

            {/* Respuestas Recibidas */}
            {reporteExistente?.id && (
              <Card className="border-green-200">
                <CardHeader className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-t-lg">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MessageCircle className="h-5 w-5" />
                    Respuestas Recibidas
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  {respuestas.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground">
                      <MessageCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Aún no hay respuestas agregadas</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead>Mensaje</TableHead>
                            <TableHead className="w-[100px] text-center">Acciones</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {respuestas.map((respuesta) => (
                            <TableRow key={respuesta.id}>
                              <TableCell>
                                <div className="max-w-[400px] truncate" title={respuesta.mensaje}>
                                  {respuesta.mensaje}
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex justify-center gap-1">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => handleAbrirModalRespuesta(respuesta)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                    onClick={() => handleEliminarRespuesta(respuesta.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  <div className="text-center mt-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-green-500 text-green-600 hover:bg-green-50"
                      onClick={() => handleAbrirModalRespuesta()}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Agregar Respuesta
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Botón Excel */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-green-600">
                  <FileSpreadsheet className="h-5 w-5" />
                  Exportar a Excel
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <FormLabel>Habilitar botón de Excel en reporte</FormLabel>
                    <FormDescription>Muestra un botón de descarga de Excel en el reporte</FormDescription>
                  </div>
                  <FormField
                    control={form.control}
                    name="habilitar_boton_excel"
                    render={({ field }) => (
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    )}
                  />
                </div>
                {habilitarExcel && (
                  <FormField
                    control={form.control}
                    name="url_excel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>URL del archivo Excel</FormLabel>
                        <FormControl>
                          <Input type="url" {...field} placeholder="https://ejemplo.com/archivo.xlsx" />
                        </FormControl>
                        <FormDescription>URL del archivo Excel para descarga</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </CardContent>
            </Card>
            </>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createReporte.isPending || isUploading}
                className="bg-green-600 hover:bg-green-700"
              >
                {(createReporte.isPending || isUploading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {esRedireccion
                  ? 'Guardar Redirección'
                  : tieneReporte ? 'Actualizar Reporte' : 'Guardar Reporte'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
        )}
      </DialogContent>

      {/* Modal para agregar/editar respuesta */}
      <Dialog open={respuestaModalOpen} onOpenChange={setRespuestaModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-green-600" />
              {respuestaEditando ? 'Editar Respuesta' : 'Agregar Respuesta'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Mensaje</label>
              <Textarea
                value={mensajeRespuesta}
                onChange={(e) => setMensajeRespuesta(e.target.value)}
                placeholder="Contenido de la respuesta recibida"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRespuestaModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              className="bg-green-600 hover:bg-green-700"
              onClick={handleGuardarRespuesta}
              disabled={createRespuesta.isPending || updateRespuesta.isPending}
            >
              {(createRespuesta.isPending || updateRespuesta.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {respuestaEditando ? 'Actualizar' : 'Agregar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  )
}
